import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Button, Col, Form, Row, Tab, Table, Tabs } from "react-bootstrap";
import Select from "react-select";

import ConditionalButton from "../../../layout/conditional-button";
import { loading, send as pageInfo } from "../../../actions/page";
import { get as getEmailTemplates } from "../../../actions/email.template";
import {
  assignWorkflow,
  campaigns as getCampaigns,
  deleteCampaign,
  deleteWorkflow,
  deleteWorkflowAssignment,
  enforceCampaigns,
  enforceLeadWorkflow,
  events as getEvents,
  eventWorkflows,
  saveCampaign,
  saveWorkflow,
  updateWorkflowAssignment,
  workflows as getWorkflows,
} from "../../../actions/lead.workflows";
import { withNavigation } from "../../../utils/wrapper";

const emptyWorkflow = () => ({
  uuid: null,
  name: "lead-follow-up-email",
  caption: "Lead Follow-Up Email",
  eventKey: "",
  enabled: true,
  stepsText: JSON.stringify([
    {
      ACTION: "email/send",
      BODY: {
        name: "$lead-follow-up",
        recipient: "person.email",
        json: {
          Lead: "lead",
          Person: "person",
        },
        noteTargetUuid: "uuid",
        noteTargetType: "$person",
      },
    },
  ], null, 2),
});

const emptyCampaignStep = (index = 0) => ({
  key: `step-${index + 1}`,
  caption: `Step ${index + 1}`,
  offsetDays: index,
  emailTemplate: "",
  subject: "",
  enabled: true,
});

const emptyCampaign = () => ({
  uuid: null,
  name: "lead-default-campaign",
  caption: "Lead Default Campaign",
  description: "",
  default: false,
  selectable: true,
  enabled: true,
  sortOrder: 0,
  steps: [emptyCampaignStep()],
});

const parseJson = (value, fallback) => {
  try {
    return JSON.parse(value || "null") || fallback;
  } catch (_error) {
    return fallback;
  }
};

const toWorkflowForm = (workflow, assignments = {}) => {
  const eventKey = Object.keys(assignments).find((key) => (
    assignments[key] || []
  ).some((item) => item.workflowUuid === workflow.uuid)) || "";

  return {
    uuid: workflow.uuid,
    name: workflow.name || "",
    caption: workflow.caption || "",
    eventKey,
    enabled: workflow.enabled !== false,
    stepsText: JSON.stringify(workflow.steps || [], null, 2),
  };
};

const toCampaignForm = (campaign) => ({
  uuid: campaign.uuid,
  name: campaign.name || "",
  caption: campaign.caption || "",
  description: campaign.description || "",
  default: Boolean(campaign.default),
  selectable: campaign.selectable !== false,
  enabled: campaign.enabled !== false,
  sortOrder: Number(campaign.sortOrder || 0),
  steps: Array.isArray(campaign.meta?.steps) && campaign.meta.steps.length
    ? campaign.meta.steps.map((step, index) => ({ ...emptyCampaignStep(index), ...step }))
    : [emptyCampaignStep()],
});

class LeadWorkflowEditor extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      tab: "workflows",
      events: [],
      workflows: [],
      assignments: {},
      campaigns: [],
      emailTemplates: [],
      workflow: emptyWorkflow(),
      campaign: emptyCampaign(),
      preview: "",
    };
  }

  componentDidMount = async () => {
    await this.props.pageInfo({ title: "Lead Workflow", breadcrumb: "Lead Workflow" });
    await this.loadData();
  };

  loadData = async () => {
    this.props.loading(true);
    try {
      const [eventData, workflows, campaigns, emailTemplates] = await Promise.all([
        this.props.getEvents(),
        this.props.getWorkflows(),
        this.props.getCampaigns(),
        this.props.getEmailTemplates(),
      ]);
      const leadEvents = (eventData?.items || []).filter((event) => event.shape === "lead");
      const assignments = {};

      for (const event of leadEvents) {
        const result = await this.props.eventWorkflows(event.key);
        assignments[event.key] = result?.items || [];
      }

      this.setState({
        events: leadEvents,
        workflows: (workflows || []).filter((workflow) => workflow.shape === "lead"),
        assignments,
        campaigns: campaigns || [],
        emailTemplates: emailTemplates || [],
      });
    } finally {
      this.props.loading(false);
    }
  };

  workflowEventOptions = () => this.state.events.map((event) => ({
    value: event.key,
    label: event.caption || event.key,
  }));

  emailTemplateOptions = () => this.state.emailTemplates.map((template) => ({
    value: template.name || template.caption || template.uuid,
    label: template.caption || template.name || template.uuid,
  }));

  setWorkflow = (field, value) => {
    this.setState((prev) => ({
      workflow: {
        ...prev.workflow,
        [field]: value,
      },
    }));
  };

  setCampaign = (field, value) => {
    this.setState((prev) => ({
      campaign: {
        ...prev.campaign,
        [field]: value,
      },
    }));
  };

  onSelectWorkflow = (workflow) => {
    this.setState({
      workflow: toWorkflowForm(workflow, this.state.assignments),
      preview: "",
    });
  };

  onSelectCampaign = (campaign) => {
    this.setState({
      campaign: toCampaignForm(campaign),
      preview: "",
    });
  };

  onNewWorkflow = () => this.setState({ workflow: emptyWorkflow(), preview: "" });

  onNewCampaign = () => this.setState({ campaign: emptyCampaign(), preview: "" });

  onSaveWorkflow = async () => {
    const steps = parseJson(this.state.workflow.stepsText, null);
    if (!Array.isArray(steps)) {
      this.setState({ preview: "Workflow steps must be valid JSON array." });
      return;
    }

    this.props.loading(true);
    try {
      const result = await this.props.saveWorkflow({
        uuid: this.state.workflow.uuid,
        name: this.state.workflow.name,
        caption: this.state.workflow.caption,
        shape: "lead",
        steps,
        enabled: this.state.workflow.enabled,
        meta: { editor: "lead-workflow" },
      });

      if (!result?.success || !result?.item?.uuid) {
        this.setState({ preview: JSON.stringify(result || {}, null, 2) });
        return;
      }

      await this.syncWorkflowAssignment(result.item.uuid);
      await this.loadData();
      this.setState((prev) => ({
        workflow: {
          ...prev.workflow,
          uuid: result.item.uuid,
        },
        preview: JSON.stringify(result, null, 2),
      }));
    } finally {
      this.props.loading(false);
    }
  };

  syncWorkflowAssignment = async (workflowUuid) => {
    const nextEventKey = this.state.workflow.eventKey;

    for (const [eventKey, items] of Object.entries(this.state.assignments)) {
      const assigned = (items || []).some((item) => item.workflowUuid === workflowUuid);
      if (assigned && eventKey !== nextEventKey) {
        await this.props.deleteWorkflowAssignment({ eventKey, workflowUuid });
      }
    }

    if (!nextEventKey) return;

    const existing = (this.state.assignments[nextEventKey] || []).some((item) => item.workflowUuid === workflowUuid);
    if (existing) {
      await this.props.updateWorkflowAssignment({
        eventKey: nextEventKey,
        workflowUuid,
        enabled: this.state.workflow.enabled,
      });
    } else {
      await this.props.assignWorkflow({
        eventKey: nextEventKey,
        workflowUuid,
        enabled: this.state.workflow.enabled,
      });
    }
  };

  onDeleteWorkflow = async () => {
    if (!this.state.workflow.uuid) return;
    this.props.loading(true);
    try {
      await this.props.deleteWorkflow(this.state.workflow.uuid);
      await this.loadData();
      this.onNewWorkflow();
    } finally {
      this.props.loading(false);
    }
  };

  onPreviewWorkflow = async () => {
    const result = await this.props.enforceLeadWorkflow({ limit: 25, dryRun: true, detail: true });
    this.setState({ preview: JSON.stringify(result || {}, null, 2) });
  };

  onCampaignStepChange = (index, field, value) => {
    this.setState((prev) => {
      const steps = [...(prev.campaign.steps || [])];
      steps[index] = {
        ...steps[index],
        [field]: value,
      };

      return {
        campaign: {
          ...prev.campaign,
          steps,
        },
      };
    });
  };

  onAddCampaignStep = () => {
    this.setState((prev) => ({
      campaign: {
        ...prev.campaign,
        steps: [
          ...(prev.campaign.steps || []),
          emptyCampaignStep((prev.campaign.steps || []).length),
        ],
      },
    }));
  };

  onRemoveCampaignStep = (index) => {
    this.setState((prev) => ({
      campaign: {
        ...prev.campaign,
        steps: (prev.campaign.steps || []).filter((_, i) => i !== index),
      },
    }));
  };

  onSaveCampaign = async () => {
    const campaign = this.state.campaign;
    this.props.loading(true);
    try {
      const result = await this.props.saveCampaign({
        uuid: campaign.uuid,
        name: campaign.name,
        caption: campaign.caption,
        description: campaign.description,
        default: campaign.default,
        selectable: campaign.selectable,
        enabled: campaign.enabled,
        sortOrder: campaign.sortOrder,
        meta: {
          steps: (campaign.steps || []).map((step, index) => ({
            ...step,
            sortOrder: index,
            offsetDays: Number(step.offsetDays || 0),
          })),
        },
      });

      await this.loadData();
      if (result?.item) this.setState({ campaign: toCampaignForm(result.item) });
      this.setState({ preview: JSON.stringify(result || {}, null, 2) });
    } finally {
      this.props.loading(false);
    }
  };

  onDeleteCampaign = async () => {
    if (!this.state.campaign.uuid) return;
    this.props.loading(true);
    try {
      await this.props.deleteCampaign(this.state.campaign.uuid);
      await this.loadData();
      this.onNewCampaign();
    } finally {
      this.props.loading(false);
    }
  };

  onPreviewCampaigns = async () => {
    const result = await this.props.enforceCampaigns({
      limit: 25,
      dryRun: true,
      detail: true,
      campaignUuid: this.state.campaign.uuid,
    });
    this.setState({ preview: JSON.stringify(result || {}, null, 2) });
  };

  renderWorkflowList = () => (
    <div className="border-end pe-3">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h2 className="small-title mb-0">Workflows</h2>
        <Button variant="link" size="sm" className="p-0" onClick={this.onNewWorkflow}>New</Button>
      </div>
      {(this.state.workflows || []).map((workflow) => (
        <button
          type="button"
          key={workflow.uuid}
          className={`btn btn-link d-block text-start px-0 py-2 w-100 ${workflow.uuid === this.state.workflow.uuid ? "fw-bold" : ""}`}
          onClick={() => this.onSelectWorkflow(workflow)}
        >
          {workflow.caption || workflow.name}
          <div className="text-muted small">{workflow.enabled === false ? "Disabled" : "Enabled"}</div>
        </button>
      ))}
    </div>
  );

  renderWorkflowEditor = () => {
    const eventValue = this.workflowEventOptions().find((option) => option.value === this.state.workflow.eventKey) || null;

    return (
      <Row className="g-3">
        <Col xs="12" md="3">{this.renderWorkflowList()}</Col>
        <Col xs="12" md="9">
          <Row className="g-3">
            <Col xs="12" md="6">
              <div className="top-label">
                <Form.Control value={this.state.workflow.caption} onChange={(e) => this.setWorkflow("caption", e.target.value)} />
                <Form.Label>CAPTION</Form.Label>
              </div>
            </Col>
            <Col xs="12" md="6">
              <div className="top-label">
                <Form.Control value={this.state.workflow.name} onChange={(e) => this.setWorkflow("name", e.target.value)} />
                <Form.Label>NAME</Form.Label>
              </div>
            </Col>
            <Col xs="12" md="8">
              <div className="top-label">
                <Select
                  classNamePrefix="react-select"
                  options={this.workflowEventOptions()}
                  value={eventValue}
                  onChange={(option) => this.setWorkflow("eventKey", option?.value || "")}
                  isClearable
                />
                <Form.Label>EVENT</Form.Label>
              </div>
            </Col>
            <Col xs="12" md="4" className="d-flex align-items-center">
              <Form.Check
                type="switch"
                id="workflow.enabled"
                label="Enabled"
                checked={Boolean(this.state.workflow.enabled)}
                onChange={(e) => this.setWorkflow("enabled", e.target.checked)}
              />
            </Col>
            <Col xs="12">
              <div className="top-label">
                <Form.Control
                  as="textarea"
                  rows={12}
                  value={this.state.workflow.stepsText}
                  onChange={(e) => this.setWorkflow("stepsText", e.target.value)}
                  style={{ fontFamily: "monospace" }}
                />
                <Form.Label>STEPS JSON</Form.Label>
              </div>
            </Col>
            <Col xs="12" className="text-end">
              <ConditionalButton enabledVariant="outline-secondary" display enabled onEnabledClick={this.onPreviewWorkflow} icon="search">Dry Run</ConditionalButton>{" "}
              <ConditionalButton enabledVariant="outline-danger" display={Boolean(this.state.workflow.uuid)} enabled onEnabledClick={this.onDeleteWorkflow} icon="bin">Delete</ConditionalButton>{" "}
              <ConditionalButton enabledVariant="outline-primary" display enabled onEnabledClick={this.onSaveWorkflow} icon="save">Save</ConditionalButton>
            </Col>
          </Row>
        </Col>
      </Row>
    );
  };

  renderCampaignList = () => (
    <div className="lead-workflow-list">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="small-title mb-0">Campaigns</h2>
        <Button variant="outline-primary" size="sm" onClick={this.onNewCampaign}>New</Button>
      </div>
      <div className="lead-workflow-list-items">
        {(this.state.campaigns || []).map((campaign) => (
          <button
            type="button"
            key={campaign.uuid}
            className={`lead-workflow-list-item ${campaign.uuid === this.state.campaign.uuid ? "active" : ""}`}
            onClick={() => this.onSelectCampaign(campaign)}
          >
            <span className="lead-workflow-list-title">{campaign.caption || campaign.name}</span>
            <span className="lead-workflow-list-meta">
              {campaign.default ? "Default" : "Selectable"} / {campaign.enabled === false ? "Disabled" : "Enabled"}
            </span>
          </button>
        ))}
      </div>
    </div>
  );

  renderCampaignStep = (step, index) => {
    const emailValue = this.emailTemplateOptions().find((option) => option.value === step.emailTemplate) || null;

    return (
      <tr key={`campaign_step_${index}`}>
        <td>
          <Form.Control size="sm" value={step.caption || ""} onChange={(e) => this.onCampaignStepChange(index, "caption", e.target.value)} />
        </td>
        <td>
          <Form.Control size="sm" value={step.key || ""} onChange={(e) => this.onCampaignStepChange(index, "key", e.target.value)} />
        </td>
        <td className="lead-campaign-offset-cell">
          <Form.Control size="sm" type="number" value={step.offsetDays || 0} onChange={(e) => this.onCampaignStepChange(index, "offsetDays", e.target.value)} />
        </td>
        <td className="lead-campaign-template-cell">
          <Select
            classNamePrefix="react-select"
            className="lead-campaign-step-select"
            options={this.emailTemplateOptions()}
            value={emailValue}
            onChange={(option) => this.onCampaignStepChange(index, "emailTemplate", option?.value || "")}
            isClearable
            menuPosition="fixed"
          />
        </td>
        <td>
          <Form.Control size="sm" value={step.subject || ""} onChange={(e) => this.onCampaignStepChange(index, "subject", e.target.value)} />
        </td>
        <td className="text-end lead-campaign-step-action-cell">
          <Button variant="link" size="sm" className="p-0" onClick={() => this.onRemoveCampaignStep(index)}>Remove</Button>
        </td>
      </tr>
    );
  };

  renderCampaignEditor = () => (
    <Row className="g-4 lead-campaign-editor">
      <Col xs="12" lg="3">{this.renderCampaignList()}</Col>
      <Col xs="12" lg="9">
        <section className="lead-campaign-section">
          <div className="lead-campaign-section-title">Campaign Fields</div>
          <Row className="g-4">
            <Col xs="12" xl="7">
              <div className="top-label">
                <Form.Control value={this.state.campaign.caption} onChange={(e) => this.setCampaign("caption", e.target.value)} />
                <Form.Label>CAPTION</Form.Label>
              </div>
              <div className="top-label">
                <Form.Control value={this.state.campaign.name} onChange={(e) => this.setCampaign("name", e.target.value)} />
                <Form.Label>NAME</Form.Label>
              </div>
              <div className="top-label">
                <Form.Control as="textarea" rows={3} value={this.state.campaign.description || ""} onChange={(e) => this.setCampaign("description", e.target.value)} />
                <Form.Label>DESCRIPTION</Form.Label>
              </div>
              <div className="top-label lead-campaign-sort-row">
                <Form.Control className="lead-campaign-sort-control" type="number" value={this.state.campaign.sortOrder} onChange={(e) => this.setCampaign("sortOrder", e.target.value)} />
                <Form.Label>SORT</Form.Label>
              </div>
            </Col>
            <Col xs="12" xl="5">
              <div className="lead-campaign-switches">
                <div className="lead-campaign-switch-row">
                  <Form.Label>Default:</Form.Label>
                  <Form.Check type="switch" id="campaign.default" label={this.state.campaign.default ? "On" : "Off"} checked={Boolean(this.state.campaign.default)} onChange={(e) => this.setCampaign("default", e.target.checked)} />
                </div>
                <div className="lead-campaign-switch-row">
                  <Form.Label>Selectable:</Form.Label>
                  <Form.Check type="switch" id="campaign.selectable" label={this.state.campaign.selectable ? "On" : "Off"} checked={Boolean(this.state.campaign.selectable)} onChange={(e) => this.setCampaign("selectable", e.target.checked)} />
                </div>
                <div className="lead-campaign-switch-row">
                  <Form.Label>Enabled:</Form.Label>
                  <Form.Check type="switch" id="campaign.enabled" label={this.state.campaign.enabled ? "On" : "Off"} checked={Boolean(this.state.campaign.enabled)} onChange={(e) => this.setCampaign("enabled", e.target.checked)} />
                </div>
              </div>
            </Col>
          </Row>
        </section>

        <section className="lead-campaign-section">
          <div className="d-flex align-items-center justify-content-between gap-3 mb-3">
            <div className="lead-campaign-section-title mb-0">Steps</div>
            <Button variant="outline-primary" size="sm" onClick={this.onAddCampaignStep}>Add Step</Button>
          </div>
          <div className="table-responsive lead-campaign-table-wrap">
            <Table bordered hover className="lead-campaign-steps-table mb-0">
              <thead>
                <tr>
                  <th>Caption</th>
                  <th>Key</th>
                  <th>Offset Days</th>
                  <th>Email Template</th>
                  <th>Subject Override</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {(this.state.campaign.steps || []).map(this.renderCampaignStep)}
              </tbody>
            </Table>
          </div>
        </section>

        <div className="text-end">
          <ConditionalButton enabledVariant="outline-secondary" display enabled onEnabledClick={this.onPreviewCampaigns} icon="search">Dry Run</ConditionalButton>{" "}
          <ConditionalButton enabledVariant="outline-danger" display={Boolean(this.state.campaign.uuid)} enabled onEnabledClick={this.onDeleteCampaign} icon="bin">Delete</ConditionalButton>{" "}
          <ConditionalButton enabledVariant="outline-primary" display enabled onEnabledClick={this.onSaveCampaign} icon="save">Save</ConditionalButton>
        </div>
      </Col>
    </Row>
  );

  render() {
    return (
      <>
        <Col xs="12">
          <Tabs activeKey={this.state.tab} onSelect={(tab) => this.setState({ tab })} className="mb-4">
            <Tab eventKey="workflows" title="Workflows">
              {this.renderWorkflowEditor()}
            </Tab>
            <Tab eventKey="campaigns" title="Campaigns">
              {this.renderCampaignEditor()}
            </Tab>
          </Tabs>
        </Col>

        {this.state.preview ? (
          <Col xs="12" className="mt-4">
            <div className="top-label">
              <Form.Control as="textarea" rows={8} readOnly value={this.state.preview} style={{ fontFamily: "monospace" }} />
              <Form.Label>RESULT</Form.Label>
            </div>
          </Col>
        ) : null}
      </>
    );
  }
}

LeadWorkflowEditor.propTypes = {
  assignWorkflow: PropTypes.func.isRequired,
  deleteCampaign: PropTypes.func.isRequired,
  deleteWorkflow: PropTypes.func.isRequired,
  deleteWorkflowAssignment: PropTypes.func.isRequired,
  enforceCampaigns: PropTypes.func.isRequired,
  enforceLeadWorkflow: PropTypes.func.isRequired,
  eventWorkflows: PropTypes.func.isRequired,
  getCampaigns: PropTypes.func.isRequired,
  getEmailTemplates: PropTypes.func.isRequired,
  getEvents: PropTypes.func.isRequired,
  getWorkflows: PropTypes.func.isRequired,
  loading: PropTypes.func.isRequired,
  pageInfo: PropTypes.func.isRequired,
  saveCampaign: PropTypes.func.isRequired,
  saveWorkflow: PropTypes.func.isRequired,
  updateWorkflowAssignment: PropTypes.func.isRequired,
};

export default withNavigation(connect(null, {
  assignWorkflow,
  deleteCampaign,
  deleteWorkflow,
  deleteWorkflowAssignment,
  enforceCampaigns,
  enforceLeadWorkflow,
  eventWorkflows,
  getCampaigns,
  getEmailTemplates,
  getEvents,
  getWorkflows,
  loading,
  pageInfo,
  saveCampaign,
  saveWorkflow,
  updateWorkflowAssignment,
})(LeadWorkflowEditor));
